import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Building2,
  ExternalLink,
  FolderKanban,
  GraduationCap,
  Award,
  Handshake,
  Inbox,
  LayoutDashboard,
  Layers,
  LifeBuoy,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Megaphone,
  MessageSquareQuote,
  Package,
  Pencil,
  Percent,
  Plus,
  RotateCcw,
  Settings,
  ShoppingBag,
  Sparkles,
  UserCog,
  Tags,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail, imgUrl, uploadImage, usd } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  "Laptops & Computers",
  "Printers & Scanners",
  "UPS & Power",
  "Networking",
  "Security & CCTV",
  "Communication",
  "Software & Licenses",
  "Servers & Storage",
  "Bundles",
  "Services",
  "Cloud Hosting",
];
const STOCK_STATUSES = [["in_stock", "In stock"], ["low_stock", "Low stock"], ["out_of_stock", "Out of stock"]];
const ORDER_STATUSES = ["awaiting_payment", "pending", "paid", "confirmed", "completed", "cancelled", "quote_requested"];
const PIE_COLORS = ["#ED1C24", "#FFC61A", "#475569", "#94A3B8"];
const EMPTY_PRODUCT = { name: "", category: CATEGORIES[0], price: 0, stock: 0, image: "", catalogue: "", description: "", featured: false, brand: "", stock_status: "in_stock", is_hidden: false, licenses: "", availability: "ready", lead_time: "", arrival_date: "" };

function BulkAvailability({ categories, onDone }) {
  const [category, setCategory] = useState(categories[0]);
  const [availability, setAvailability] = useState("ready");
  const [leadTime, setLeadTime] = useState("");
  const [arrival, setArrival] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/products/bulk-availability", {
        category, availability, lead_time: leadTime, arrival_date: arrival,
      });
      toast.success(`${r.data.updated} product(s) in "${category}" updated`);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="bulk-availability" className="bg-[#0F172A] border border-white/10 p-5 mb-6 flex flex-wrap items-end gap-3">
      <p className="w-full text-[11px] tracking-[0.2em] uppercase text-slate-500">Bulk availability — set a whole category at once</p>
      <Select value={category} onValueChange={setCategory}>
        <SelectTrigger data-testid="bulk-category-select" className={`${field} w-56 h-9 text-xs`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
          {categories.map((c) => <SelectItem key={c} value={c} data-testid={`bulk-cat-${c}`}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={availability} onValueChange={setAvailability}>
        <SelectTrigger data-testid="bulk-availability-select" className={`${field} w-40 h-9 text-xs`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
          <SelectItem value="ready" data-testid="bulk-ready">Ready stock</SelectItem>
          <SelectItem value="preorder" data-testid="bulk-preorder">Pre-order</SelectItem>
        </SelectContent>
      </Select>
      {availability === "preorder" && (
        <>
          <Input data-testid="bulk-lead-time-input" placeholder="Lead time (e.g. 2-3 weeks)" value={leadTime}
            onChange={(e) => setLeadTime(e.target.value)} className={`${field} w-44 h-9 text-xs`} />
          <Input data-testid="bulk-arrival-input" type="date" value={arrival}
            onChange={(e) => setArrival(e.target.value)} className={`${field} w-40 h-9 text-xs`} />
        </>
      )}
      <button
        data-testid="bulk-apply-btn"
        onClick={apply}
        disabled={saving}
        className="text-xs border border-white/15 text-slate-200 hover:border-[#ED1C24] hover:text-white disabled:opacity-50 px-5 py-2.5 transition-colors duration-200"
      >
        {saving ? "Applying..." : "Apply to category"}
      </button>
    </div>
  );
}
const EMPTY_BRAND = { name: "", logo: "", description: "" };
const EMPTY_MEMBER = { name: "", role: "", bio: "", photo: "", email: "", phone: "" };
const field = "bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-11";

function ImageUploadField({ value, onChange, testId, accept = "image/*", placeholder = "Image URL or upload a file" }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const isPdf = value && (value.toLowerCase().endsWith(".pdf") || value.includes(".pdf"));
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          data-testid={`${testId}-url`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={field}
        />
        <label
          data-testid={`${testId}-upload-label`}
          className="shrink-0 inline-flex items-center px-4 border border-white/15 text-xs text-slate-300 hover:border-[#ED1C24] hover:text-white cursor-pointer transition-colors duration-300"
        >
          {uploading ? "Uploading..." : "Upload"}
          <input
            data-testid={`${testId}-file`}
            type="file"
            accept={accept}
            className="hidden"
            disabled={uploading}
            onChange={handleFile}
          />
        </label>
      </div>
      {value && isPdf && (
        <p className="text-xs text-emerald-400 flex items-center gap-2">
          PDF attached — <a href={imgUrl(value)} target="_blank" rel="noopener noreferrer" className="underline">preview</a>
        </p>
      )}
      {value && !isPdf && (
        <img src={imgUrl(value)} alt="Preview" className="h-14 w-14 object-cover border border-white/10" />
      )}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: "#0F172A", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 },
  labelStyle: { color: "#94A3B8" },
  itemStyle: { color: "#F8FAFC" },
};

function GalleryField({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const images = (value || []).map((g) => (typeof g === "string" ? { url: g, caption: "" } : g));

  const emit = (next) => onChange(next.map((g) => ({ url: g.url, caption: g.caption || "" })));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      emit([...images, { url, caption: "" }]);
      toast.success("Photo added to gallery");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">Gallery photos ({images.length}) — the newest photo becomes the activity's cover</p>
        <label
          data-testid={`${testId}-add`}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-xs text-slate-300 hover:border-[#ED1C24] hover:text-white cursor-pointer transition-colors duration-300"
        >
          <Plus size={12} /> {uploading ? "Uploading..." : "Add photo"}
          <input data-testid={`${testId}-file`} type="file" accept="image/*" className="hidden" disabled={uploading} onChange={handleFile} />
        </label>
      </div>
      {images.length > 0 && (
        <div className="space-y-2">
          {images.map((g, i) => (
            <div key={i} className="flex items-center gap-3 border border-white/10 bg-white/[0.02] p-2">
              <img src={imgUrl(g.url)} alt="" className="w-14 h-14 object-cover border border-white/10 shrink-0" />
              <input
                data-testid={`${testId}-caption-${i}`}
                value={g.caption || ""}
                onChange={(e) => emit(images.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))}
                placeholder="Caption (optional, shown with the photo)"
                className="flex-1 min-w-0 bg-transparent border-0 border-b border-white/10 focus:border-[#ED1C24] text-xs text-slate-200 placeholder:text-slate-600 py-1.5 outline-none transition-colors"
              />
              <button
                type="button"
                data-testid={`${testId}-remove-${i}`}
                onClick={() => emit(images.filter((_, j) => j !== i))}
                className="w-7 h-7 shrink-0 flex items-center justify-center text-slate-500 hover:text-[#ED1C24] transition-colors"
                aria-label="Remove photo"
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-[#0F172A] border border-white/10 p-6">
      <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{label}</p>
      <p className="font-display text-3xl text-white mt-2">{value}</p>
    </div>
  );
}

const VISITOR_PERIODS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 Days" },
  { id: "30d", label: "Last 30 Days" },
];

function VisitorOverview() {
  const [period, setPeriod] = useState("7d");
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setData(null);
    setFailed(false);
    api
      .get("/admin/analytics/visitors", { params: { period } })
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
  }, [period]);

  return (
    <div data-testid="visitor-overview" className="bg-[#0F172A] border border-white/10 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">Visitor Overview</p>
        <div className="flex gap-2">
          {VISITOR_PERIODS.map((p) => (
            <button
              key={p.id}
              data-testid={`period-${p.id}`}
              onClick={() => setPeriod(p.id)}
              className={`text-xs px-4 py-2 border transition-colors duration-300 ${
                period === p.id
                  ? "bg-[#ED1C24] border-[#ED1C24] text-white"
                  : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {data === null && !failed && <div className="h-48 bg-white/[0.03] animate-pulse" />}
      {failed && <p className="text-slate-500 text-sm">Failed to load visitor data.</p>}

      {data && data.total_visits === 0 && (
        <div data-testid="visitor-empty-state" className="border border-dashed border-white/15 p-10 text-center">
          <p className="text-slate-400 text-sm">No visitor data yet for this period.</p>
          <p className="text-slate-600 text-xs mt-1">
            Visits are recorded automatically as people browse the site.
          </p>
        </div>
      )}

      {data && data.total_visits > 0 && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <MetricCard label="Total Visits" value={data.total_visits} testId="vo-total-visits" />
            <MetricCard label="Unique Visitors" value={data.unique_visitors} testId="vo-unique-visitors" />
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-4">
                {period === "today" ? "Hourly visitor trend" : "Daily visitor trend"}
              </p>
              <div data-testid="vo-trend-chart" className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.trend}>
                    <defs>
                      <linearGradient id="voFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ED1C24" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#ED1C24" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: "#64748B", fontSize: 10 }}
                      tickFormatter={(d) => (period === "today" ? d : d.slice(5))}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip {...tooltipStyle} />
                    <Area type="monotone" dataKey="count" stroke="#ED1C24" strokeWidth={2} fill="url(#voFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-4">
                Top 5 products by page visits
              </p>
              {data.top_products.length === 0 ? (
                <p data-testid="vo-top-products-empty" className="text-slate-600 text-xs">
                  No product page visits in this period yet.
                </p>
              ) : (
                <div data-testid="vo-top-products" className="space-y-4">
                  {data.top_products.map((p, i) => (
                    <div key={p.product_id} data-testid={`vo-top-product-${i}`}>
                      <div className="flex justify-between gap-3 text-xs mb-1.5">
                        <span className="text-slate-300 truncate">
                          <span className="text-[#ED1C24] font-semibold mr-2">#{i + 1}</span>
                          {p.name}
                        </span>
                        <span className="text-slate-500 shrink-0">{p.count}</span>
                      </div>
                      <div className="h-1.5 bg-white/5">
                        <div
                          className="h-full bg-gradient-to-r from-[#ED1C24] to-[#FFC61A]"
                          style={{ width: `${(p.count / data.top_products[0].count) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <div className="h-64 bg-white/[0.03] animate-pulse" />;
  if (data === false) return <p className="text-slate-500 text-sm">Failed to load analytics.</p>;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <MetricCard label="Total Visits" value={data.total_visits} testId="metric-total-visits" />
        <MetricCard label="Visits Today" value={data.visits_today} testId="metric-visits-today" />
        <MetricCard label="Orders" value={data.orders} testId="metric-orders" />
        <MetricCard label={`Kit Sales (${data.kit_orders ?? 0})`} value={usd(data.kit_revenue ?? 0)} testId="metric-kit-sales" />
        <MetricCard label="Products" value={data.products} testId="metric-products" />
        <MetricCard label="Messages" value={data.messages} testId="metric-messages" />
      </div>

      <VisitorOverview />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#0F172A] border border-white/10 p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-6">
            Visits — last 14 days
          </p>
          <div data-testid="chart-daily-visits" className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.daily}>
                <defs>
                  <linearGradient id="redFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ED1C24" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#ED1C24" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Area type="monotone" dataKey="count" stroke="#ED1C24" strokeWidth={2} fill="url(#redFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#0F172A] border border-white/10 p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-6">
            Engagement by device
          </p>
          <div data-testid="chart-devices" className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.by_device} dataKey="count" nameKey="device" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {data.by_device.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {data.by_device.map((d, i) => (
              <span key={d.device} className="flex items-center gap-2 text-xs text-slate-400">
                <span className="w-2.5 h-2.5" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                {d.device} ({d.count})
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#0F172A] border border-white/10 p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-6">
            Visits by country
          </p>
          <div data-testid="chart-countries" className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_country} layout="vertical">
                <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="country" width={110} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="count" fill="#ED1C24" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#0F172A] border border-white/10 p-6">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-4">
            Recent visits
          </p>
          <div data-testid="recent-visits-list" className="space-y-3 max-h-64 overflow-y-auto">
            {data.recent.map((v) => (
              <div key={v.id} className="flex justify-between gap-3 text-xs border-b border-white/5 pb-2">
                <span className="text-slate-300 truncate">{v.path}</span>
                <span className="text-slate-500 shrink-0">{v.country} · {v.device}</span>
              </div>
            ))}
            {data.recent.length === 0 && (
              <p className="text-slate-600 text-xs">No visits tracked yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState(null);
  const [brands, setBrands] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translatedCount, setTranslatedCount] = useState(0);

  const translateAll = async () => {
    setTranslating(true);
    setTranslatedCount(0);
    try {
      let total = 0;
      for (let guard = 0; guard < 30; guard++) {
        const { data } = await api.post("/admin/translate-descriptions", { batch: 12 });
        total += data.translated;
        setTranslatedCount(total);
        if (!data.remaining) {
          toast.success(`Tetum translations complete — ${total} products`);
          load();
          return;
        }
        if (!data.translated) break;
      }
      toast.error("Translation stopped early — some descriptions may not be translated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTranslating(false);
    }
  };

  const load = useCallback(() => {
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_PRODUCT);
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, category: p.category, price: p.price, stock: p.stock, image: p.image, catalogue: p.catalogue || "", description: p.description, featured: p.featured, brand: p.brand || "", stock_status: p.stock_status || "in_stock", is_hidden: !!p.is_hidden, licenses: (p.licenses || []).join(", "), availability: p.availability || "ready", lead_time: p.lead_time || "", arrival_date: p.arrival_date || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0, stock: Number(form.stock) || 0,
        licenses: typeof form.licenses === "string" ? form.licenses.split(",").map((s) => s.trim()).filter(Boolean) : (form.licenses || []) };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-slate-400">{products?.length ?? "…"} products in catalog</p>
        <div className="flex items-center gap-3">
          <button
            data-testid="translate-tetum-btn"
            onClick={translateAll}
            disabled={translating}
            className="inline-flex items-center gap-2 border border-[#FFC61A]/40 text-[#FFC61A] hover:bg-[#FFC61A] hover:text-[#0B1120] disabled:opacity-50 text-sm font-medium px-4 py-2.5 transition-colors duration-300"
          >
            {translating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {translating ? `Translating… ${translatedCount}` : "Translate descriptions to Tetum"}
          </button>
          <button
            data-testid="add-product-btn"
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
          >
            <Plus size={15} /> Add Product
          </button>
        </div>
      </div>

      <BulkAvailability categories={CATEGORIES} onDone={load} />

      <div className="bg-[#0F172A] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-5 py-4">Product</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4">Brand</th>
              <th className="px-5 py-4">Price</th>
              <th className="px-5 py-4">Stock</th>
              <th className="px-5 py-4">Featured</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(products || []).map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <img src={imgUrl(p.image)} alt="" className="w-10 h-10 object-cover" />
                    <span className="text-slate-200">{p.name}</span>
                    {p.is_hidden && (
                      <span className="text-[10px] uppercase tracking-wider bg-white/10 text-slate-400 px-2 py-0.5">Hidden</span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-400">{p.category}</td>
                <td className="px-5 py-3 text-slate-400">{p.brand || "—"}</td>
                <td className="px-5 py-3 text-[#FFC61A]">{usd(p.price)}</td>
                <td className="px-5 py-3 text-slate-400">{p.stock}</td>
                <td className="px-5 py-3">{p.featured ? <span className="text-[#ED1C24] text-xs uppercase tracking-wider">Yes</span> : <span className="text-slate-600">—</span>}</td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#FFC61A] transition-colors" aria-label="Edit">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors" aria-label="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products !== null && products.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-10">No products yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="product-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Product" : "Add Product"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="product-name-input" placeholder="Product name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="product-category-select" className={`${field} w-full`}>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`category-option-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="product-price-input" type="number" min="0" step="0.01" placeholder="Price (USD)" value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} />
              <Input data-testid="product-stock-input" type="number" min="0" placeholder="Stock" value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })} className={field} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select value={form.brand || "none"} onValueChange={(v) => setForm({ ...form, brand: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="product-brand-select" className={`${field} w-full`}>
                  <SelectValue placeholder="Brand" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                  <SelectItem value="none">No brand</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b.id} value={b.name} data-testid={`brand-option-${b.id}`}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={form.stock_status} onValueChange={(v) => setForm({ ...form, stock_status: v })}>
                <SelectTrigger data-testid="product-stock-status-select" className={`${field} w-full`}>
                  <SelectValue placeholder="Stock status" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                  {STOCK_STATUSES.map(([v, label]) => (
                    <SelectItem key={v} value={v} data-testid={`stock-status-${v}`}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select value={form.availability} onValueChange={(v) => setForm({ ...form, availability: v })}>
                <SelectTrigger data-testid="product-availability-select" className={`${field} w-full`}>
                  <SelectValue placeholder="Availability" />
                </SelectTrigger>
                <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                  <SelectItem value="ready" data-testid="availability-ready">Ready stock</SelectItem>
                  <SelectItem value="preorder" data-testid="availability-preorder">Pre-order</SelectItem>
                </SelectContent>
              </Select>
              {form.availability === "preorder" ? (
                <Input data-testid="product-lead-time-input" placeholder="Lead time (e.g. 2-3 weeks)" value={form.lead_time}
                  onChange={(e) => setForm({ ...form, lead_time: e.target.value })} className={field} />
              ) : (
                <div className="flex items-center text-[11px] text-slate-500">Shown as &quot;Ready stock&quot; on the shop</div>
              )}
            </div>
            {form.availability === "preorder" && (
              <div>
                <Input data-testid="product-arrival-date-input" type="date" value={form.arrival_date}
                  onChange={(e) => setForm({ ...form, arrival_date: e.target.value })} className={field} />
                <p className="text-[11px] text-slate-500 mt-1.5">Expected arrival date (optional) — replaces the lead time on the shop when set.</p>
              </div>
            )}
            <ImageUploadField testId="product-image" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
            <ImageUploadField testId="product-catalogue" accept="application/pdf" placeholder="Catalogue / datasheet PDF URL or upload"
              value={form.catalogue} onChange={(v) => setForm({ ...form, catalogue: v })} />
            <div>
              <Input data-testid="product-licenses-input" placeholder="License labels, comma separated (e.g. Windows 11 Pro, Office 2024)" value={form.licenses}
                onChange={(e) => setForm({ ...form, licenses: e.target.value })} className={field} />
              <p className="text-[11px] text-slate-500 mt-1.5">For software and bundles — one license key per label is emailed after payment. Leave empty to use the product name.</p>
            </div>
            <div>
              <Textarea data-testid="product-description-input" placeholder="Short description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <div className="flex justify-end mt-1.5">
                <AiGenerateButton testId="product-ai-btn" kind="product_description" name={form.name}
                  context={`${form.category || ""} ${form.brand || ""}`}
                  onDone={(text) => setForm((f) => ({ ...f, description: text }))} />
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm text-slate-300">
              <Switch data-testid="product-featured-switch" checked={form.featured}
                onCheckedChange={(v) => setForm({ ...form, featured: v })} />
              Show in Featured section
            </label>
            <label className="flex items-center gap-3 text-sm text-slate-300">
              <Switch data-testid="product-hidden-switch" checked={form.is_hidden}
                onCheckedChange={(v) => setForm({ ...form, is_hidden: v })} />
              Hide from public store
            </label>
          </div>
          <DialogFooter>
            <button
              data-testid="product-save-btn"
              onClick={save}
              disabled={saving || !form.name}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : "Save Product"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}`, { status });
      toast.success(`Order ${order.order_number} → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-orders-tab" className="bg-[#0F172A] border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
            <th className="px-5 py-4">Order</th>
            <th className="px-5 py-4">Customer</th>
            <th className="px-5 py-4">Items</th>
            <th className="px-5 py-4">Total</th>
            <th className="px-5 py-4">Method</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {(orders || []).map((o) => (
            <tr key={o.id} data-testid={`admin-order-row-${o.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="px-5 py-3 text-[#FFC61A] font-medium">
                {o.order_number}
                {/^(KIT|CCTV)/.test(o.promo_code || "") && (
                  <span data-testid={`order-kit-badge-${o.id}`} className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-[#FFC61A]/15 text-[#FFC61A] border border-[#FFC61A]/40 px-2 py-0.5 align-middle">
                    Kit {o.promo_code}
                  </span>
                )}
                {o.promo_code && !/^(KIT|CCTV)/.test(o.promo_code) && (
                  <span data-testid={`order-promo-badge-${o.id}`} className="ml-2 text-[10px] font-normal uppercase tracking-wide bg-emerald-400/10 text-emerald-400 border border-emerald-400/30 px-2 py-0.5 align-middle">
                    {o.promo_code}
                  </span>
                )}
                {o.license_keys?.length > 0 && (
                  <div data-testid={`order-licenses-${o.id}`} className="mt-2 space-y-1">
                    {o.license_keys.map((k, i) => (
                      <p key={i} className="text-[11px] font-mono font-normal text-slate-400">
                        <span className="text-slate-500">{k.label}:</span> <span className="text-emerald-400">{k.key}</span>
                      </p>
                    ))}
                  </div>
                )}
              </td>
              <td className="px-5 py-3">
                <p className="text-slate-200">{o.customer.name}</p>
                <p className="text-xs text-slate-500">{o.customer.email}</p>
              </td>
              <td className="px-5 py-3 text-slate-400">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
              <td className="px-5 py-3 text-white">{usd(o.total)}</td>
              <td className="px-5 py-3 text-slate-400 capitalize">{o.method}</td>
              <td className="px-5 py-3">
                <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
                  <SelectTrigger data-testid={`order-status-${o.id}`} className="bg-[#0B1120] border-white/10 text-slate-200 rounded-none h-9 w-40 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                    {ORDER_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-5 py-3 text-slate-500 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {orders !== null && orders.length === 0 && (
        <p data-testid="no-orders-message" className="text-slate-500 text-sm text-center py-10">No orders yet.</p>
      )}
    </div>
  );
}

function Messages() {
  const [messages, setMessages] = useState(null);

  useEffect(() => {
    api.get("/admin/messages").then((r) => setMessages(r.data)).catch(() => setMessages([]));
  }, []);

  return (
    <div data-testid="admin-messages-tab" className="bg-[#0F172A] border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
            <th className="px-5 py-4">From</th>
            <th className="px-5 py-4">Subject</th>
            <th className="px-5 py-4">Message</th>
            <th className="px-5 py-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {(messages || []).map((m) => (
            <tr key={m.id} data-testid={`admin-message-row-${m.id}`} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
              <td className="px-5 py-3">
                <p className="text-slate-200">{m.name}</p>
                <p className="text-xs text-slate-500">{m.email}</p>
              </td>
              <td className="px-5 py-3 text-slate-300">{m.subject || "—"}</td>
              <td className="px-5 py-3 text-slate-400 max-w-md"><p className="line-clamp-2">{m.message}</p></td>
              <td className="px-5 py-3 text-slate-500 text-xs">{new Date(m.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {messages !== null && messages.length === 0 && (
        <p className="text-slate-500 text-sm text-center py-10">No messages yet.</p>
      )}
    </div>
  );
}

const EMPTY_BRAND_FORM = () => ({ ...EMPTY_BRAND });

function Brands() {
  const [brands, setBrands] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_BRAND);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => setBrands([]));
  }, []);
  useEffect(load, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_BRAND_FORM()); setDialogOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ name: b.name, logo: b.logo || "", description: b.description || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/brands/${editing.id}`, form);
        toast.success("Brand updated");
      } else {
        await api.post("/admin/brands", form);
        toast.success("Brand created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?`)) return;
    try {
      await api.delete(`/admin/brands/${b.id}`);
      toast.success("Brand deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-brands-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-slate-400">{brands?.length ?? "…"} brands & partners</p>
        <button
          data-testid="add-brand-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
        >
          <Plus size={15} /> Add Brand
        </button>
      </div>

      <div className="bg-[#0F172A] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-5 py-4">Brand</th>
              <th className="px-5 py-4">Description</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(brands || []).map((b) => (
              <tr key={b.id} data-testid={`admin-brand-row-${b.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {b.logo ? (
                      <img src={imgUrl(b.logo)} alt="" className="w-10 h-10 object-contain bg-white/5 p-1" />
                    ) : (
                      <span className="w-10 h-10 bg-[#ED1C24]/15 border border-[#ED1C24]/30 flex items-center justify-center font-display text-[#ED1C24] text-sm">
                        {b.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <span className="text-slate-200">{b.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-400 max-w-md"><p className="line-clamp-2">{b.description || "—"}</p></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-brand-${b.id}`} onClick={() => openEdit(b)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#FFC61A] transition-colors" aria-label="Edit">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-brand-${b.id}`} onClick={() => remove(b)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors" aria-label="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {brands !== null && brands.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-10">No brands yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="brand-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Brand" : "Add Brand"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="brand-name-input" placeholder="Brand name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <ImageUploadField testId="brand-logo" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} />
            <div>
              <Textarea data-testid="brand-description-input" placeholder="Short description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <div className="flex justify-end mt-1.5">
                <AiGenerateButton testId="brand-ai-btn" kind="brand_description" name={form.name}
                  onDone={(text) => setForm((f) => ({ ...f, description: text }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="brand-save-btn"
              onClick={save}
              disabled={saving || !form.name}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : "Save Brand"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamManager() {
  const [members, setMembers] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/team").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  }, []);
  useEffect(load, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY_MEMBER }); setDialogOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, role: m.role, bio: m.bio || "", photo: m.photo || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/team/${editing.id}`, form);
        toast.success("Team member updated");
      } else {
        await api.post("/admin/team", form);
        toast.success("Team member added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove "${m.name}" from the team?`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success("Team member removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-team-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-slate-400">{members?.length ?? "…"} team members shown on the public site</p>
        <button
          data-testid="add-member-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
        >
          <Plus size={15} /> Add Member
        </button>
      </div>

      <div className="bg-[#0F172A] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-5 py-4">Member</th>
              <th className="px-5 py-4">Role</th>
              <th className="px-5 py-4">Bio</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(members || []).map((m) => (
              <tr key={m.id} data-testid={`admin-member-row-${m.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {m.photo ? (
                      <img src={imgUrl(m.photo)} alt="" className="w-10 h-10 object-cover" />
                    ) : (
                      <span className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center font-display text-slate-500 text-sm">
                        {m.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <span className="text-slate-200">{m.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-400">{m.role}</td>
                <td className="px-5 py-3 text-slate-400 max-w-md"><p className="line-clamp-2">{m.bio || "—"}</p></td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-member-${m.id}`} onClick={() => openEdit(m)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#FFC61A] transition-colors" aria-label="Edit">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-member-${m.id}`} onClick={() => remove(m)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors" aria-label="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {members !== null && members.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-10">No team members yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="member-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Team Member" : "Add Team Member"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="member-name-input" placeholder="Full name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <Input data-testid="member-role-input" placeholder="Role (e.g. Network Engineer)" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })} className={field} />
            <ImageUploadField testId="member-photo" value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} />
            <div>
              <Textarea data-testid="member-bio-input" placeholder="Short biography" rows={3} value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <div className="flex justify-end mt-1.5">
                <AiGenerateButton testId="member-ai-btn" kind="team_bio" name={form.name} context={form.role}
                  onDone={(text) => setForm((f) => ({ ...f, bio: text }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="member-email-input" type="email" placeholder="Email (optional)" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
              <Input data-testid="member-phone-input" placeholder="Phone / WhatsApp (optional)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
            </div>
            <p className="text-[11px] text-slate-500 -mt-2">Contact details appear in the member&apos;s profile popup on the public Team section.</p>
          </div>
          <DialogFooter>
            <button
              data-testid="member-save-btn"
              onClick={save}
              disabled={saving || !form.name || !form.role}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : "Save Member"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function TestimonialsManager() {
  const [items, setItems] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ org: "", quote: "", author: "", role: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const openCreate = () => { setEditing(null); setForm({ org: "", quote: "", author: "", role: "" }); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ org: it.org, quote: it.quote, author: it.author || "", role: it.role || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/testimonials/${editing.id}`, form);
        toast.success("Testimonial updated");
      } else {
        await api.post("/admin/testimonials", form);
        toast.success("Testimonial added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete testimonial from "${it.org}"?`)) return;
    try {
      await api.delete(`/admin/testimonials/${it.id}`);
      toast.success("Testimonial deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-testimonials-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-slate-400">{items?.length ?? "…"} testimonials on the landing page</p>
        <button
          data-testid="add-testimonial-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
        >
          <Plus size={15} /> Add Testimonial
        </button>
      </div>

      <div className="bg-[#0F172A] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-5 py-4">Organization</th>
              <th className="px-5 py-4">Quote</th>
              <th className="px-5 py-4">Author</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((it) => (
              <tr key={it.id} data-testid={`admin-testimonial-row-${it.id}`} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
                <td className="px-5 py-3 text-slate-200 whitespace-nowrap">{it.org}</td>
                <td className="px-5 py-3 text-slate-400 max-w-md"><p className="line-clamp-2">{it.quote}</p></td>
                <td className="px-5 py-3">
                  <p className="text-slate-200">{it.author}</p>
                  <p className="text-xs text-slate-500">{it.role}</p>
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-testimonial-${it.id}`} onClick={() => openEdit(it)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#FFC61A] transition-colors" aria-label="Edit">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-testimonial-${it.id}`} onClick={() => remove(it)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors" aria-label="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items !== null && items.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-10">No testimonials yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="testimonial-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Testimonial" : "Add Testimonial"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="testimonial-org-input" placeholder="Organization (e.g. ChildFund Timor-Leste)" value={form.org}
              onChange={(e) => setForm({ ...form, org: e.target.value })} className={field} />
            <div>
              <Textarea data-testid="testimonial-quote-input" placeholder="Client quote" rows={4} value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <div className="flex justify-end mt-1.5">
                <AiGenerateButton testId="testimonial-ai-btn" kind="testimonial_quote" name={form.org} context={form.author}
                  onDone={(text) => setForm((f) => ({ ...f, quote: text }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="testimonial-author-input" placeholder="Author name" value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })} className={field} />
              <Input data-testid="testimonial-role-input" placeholder="Author role" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })} className={field} />
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="testimonial-save-btn"
              onClick={save}
              disabled={saving || !form.org || !form.quote}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : "Save Testimonial"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientsManager() {
  const [clients, setClients] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  }, []);
  useEffect(load, [load]);

  const openCreate = () => { setEditing(null); setForm({ name: "", logo: "" }); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, logo: c.logo || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/clients/${editing.id}`, form);
        toast.success("Client updated");
      } else {
        await api.post("/admin/clients", form);
        toast.success("Client added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success("Client deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-clients-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-slate-400">{clients?.length ?? "…"} clients shown on the landing page</p>
        <button
          data-testid="add-client-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
        >
          <Plus size={15} /> Add Client
        </button>
      </div>

      <div className="bg-[#0F172A] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-5 py-4">Client</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(clients || []).map((c) => (
              <tr key={c.id} data-testid={`admin-client-row-${c.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {c.logo ? (
                      <img src={imgUrl(c.logo)} alt="" className="w-10 h-10 object-contain bg-white p-1 rounded" />
                    ) : (
                      <span className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center font-display text-slate-500 text-sm">
                        {c.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <span className="text-slate-200">{c.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#FFC61A] transition-colors" aria-label="Edit">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors" aria-label="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {clients !== null && clients.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-10">No clients yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="client-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Client" : "Add Client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="client-name-input" placeholder="Client / organization name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <ImageUploadField testId="client-logo" value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} />
          </div>
          <DialogFooter>
            <button
              data-testid="client-save-btn"
              onClick={save}
              disabled={saving || !form.name}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : "Save Client"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TICKET_STATUSES = ["open", "in_progress", "resolved", "closed"];

function TicketRow({ tk, onStatus, PRIORITY_STYLE }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/tickets/${tk.id}/reply`, { message: reply.trim() });
      toast.success(`Reply sent to ${tk.customer.email}`);
      setReply("");
      onStatus(tk, tk.status, true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <tr data-testid={`admin-ticket-row-${tk.id}`} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
      <td className="px-5 py-3 text-[#FFC61A] font-medium whitespace-nowrap">{tk.ticket_number}</td>
      <td className="px-5 py-3">
        <p className="text-slate-200">{tk.customer.name}</p>
        <p className="text-xs text-slate-500">{tk.customer.email}{tk.customer.phone ? ` · ${tk.customer.phone}` : ""}</p>
      </td>
      <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{tk.category}</td>
      <td className={`px-5 py-3 text-xs uppercase tracking-wider ${PRIORITY_STYLE[tk.priority] || "text-slate-300"}`}>{tk.priority}</td>
      <td className="px-5 py-3 max-w-sm">
        <p className="text-slate-200">{tk.subject}</p>
        <p className="text-xs text-slate-500 mt-1">{tk.message}</p>
        {(tk.replies || []).map((r, i) => (
          <div key={i} data-testid={`ticket-reply-${tk.id}-${i}`} className="mt-2 border-l-2 border-[#FFC61A]/50 pl-3">
            <p className="text-xs text-slate-300">{r.message}</p>
            <p className="text-[10px] text-slate-600 mt-0.5">{r.by} · {new Date(r.created_at).toLocaleString()}</p>
          </div>
        ))}
        <div className="flex gap-2 mt-3">
          <Input
            data-testid={`ticket-reply-input-${tk.id}`}
            placeholder="Reply to customer (emailed)..."
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 rounded-none h-9 text-xs"
          />
          <button
            data-testid={`ticket-reply-send-${tk.id}`}
            onClick={sendReply}
            disabled={sending || !reply.trim()}
            className="text-xs border border-white/15 text-slate-300 hover:border-[#ED1C24] hover:text-white disabled:opacity-40 px-4 transition-colors duration-200 shrink-0"
          >
            {sending ? "..." : "Reply"}
          </button>
        </div>
      </td>
      <td className="px-5 py-3">
        <Select value={tk.status} onValueChange={(v) => onStatus(tk, v)}>
          <SelectTrigger data-testid={`ticket-status-${tk.id}`} className="bg-[#0B1120] border-white/10 text-slate-200 rounded-none h-9 w-36 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
            {TICKET_STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{new Date(tk.created_at).toLocaleDateString()}</td>
    </tr>
  );
}

function Tickets() {
  const [tickets, setTickets] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (ticket, status, silent = false) => {
    if (silent) {
      load();
      return;
    }
    try {
      await api.patch(`/admin/tickets/${ticket.id}`, { status });
      toast.success(`Ticket ${ticket.ticket_number} → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const PRIORITY_STYLE = {
    urgent: "text-[#ED1C24]",
    high: "text-[#FFC61A]",
    normal: "text-slate-300",
    low: "text-slate-500",
  };

  return (
    <div data-testid="admin-tickets-tab" className="bg-[#0F172A] border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
            <th className="px-5 py-4">Ticket</th>
            <th className="px-5 py-4">Customer</th>
            <th className="px-5 py-4">Category</th>
            <th className="px-5 py-4">Priority</th>
            <th className="px-5 py-4">Subject & Replies</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {(tickets || []).map((tk) => (
            <TicketRow key={tk.id} tk={tk} onStatus={setStatus} PRIORITY_STYLE={PRIORITY_STYLE} />
          ))}
        </tbody>
      </table>
      {tickets !== null && tickets.length === 0 && (
        <p data-testid="no-tickets-message" className="text-slate-500 text-sm text-center py-10">No support tickets yet.</p>
      )}
    </div>
  );
}

const ACTIVITY_KINDS = ["Training", "Project", "Workshop", "Supply & Delivery", "Event"];

function ActivitiesManager() {
  const [items, setItems] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", kind: "Training", description: "", image: "", gallery: [], location: "", date: "", starts_at: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/activities").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const openCreate = () => { setEditing(null); setForm({ title: "", kind: "Training", description: "", image: "", gallery: [], location: "", date: "", starts_at: "" }); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ title: a.title, kind: a.kind || "Training", description: a.description || "", image: a.image || "", gallery: a.gallery || [], location: a.location || "", date: a.date || "", starts_at: a.starts_at || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/activities/${editing.id}`, form);
        toast.success("Activity updated");
      } else {
        await api.post("/admin/activities", form);
        toast.success("Activity added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete activity "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/activities/${a.id}`);
      toast.success("Activity deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-activities-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-slate-400">{items?.length ?? "…"} activities shown on the landing page</p>
        <button
          data-testid="add-activity-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
        >
          <Plus size={15} /> Add Activity
        </button>
      </div>

      <div className="bg-[#0F172A] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-5 py-4">Activity</th>
              <th className="px-5 py-4">Type</th>
              <th className="px-5 py-4">Date / Location</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((a) => (
              <tr key={a.id} data-testid={`admin-activity-row-${a.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {a.image ? (
                      <img src={imgUrl(a.image)} alt="" className="w-10 h-10 object-cover" />
                    ) : (
                      <span className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center text-slate-500 text-xs font-display">
                        {a.title.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <div>
                      <p className="text-slate-200">{a.title}</p>
                      <p className="text-xs text-slate-500 line-clamp-1 max-w-xs">{a.description}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-400 whitespace-nowrap">{a.kind}</td>
                <td className="px-5 py-3 text-slate-400 text-xs">{a.date || "—"}{a.location ? ` · ${a.location}` : ""}</td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-activity-${a.id}`} onClick={() => openEdit(a)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#FFC61A] transition-colors" aria-label="Edit">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-activity-${a.id}`} onClick={() => remove(a)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors" aria-label="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items !== null && items.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-10">No activities yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="activity-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Activity" : "Add Activity"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="activity-title-input" placeholder="Title (e.g. Cybersecurity Awareness Training)" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} />
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="activity-kind-select" className={`${field} w-full`}>
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                {ACTIVITY_KINDS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`activity-kind-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <Textarea data-testid="activity-description-input" placeholder="Short description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <div className="flex justify-end mt-1.5">
                <AiGenerateButton testId="activity-ai-btn" kind="activity_description" name={form.title} context={form.kind}
                  onDone={(text) => setForm((f) => ({ ...f, description: text }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="activity-date-input" placeholder="Date (e.g. March 2026)" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} className={field} />
              <Input data-testid="activity-location-input" placeholder="Location (e.g. Dili)" value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })} className={field} />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 mb-1.5">Start date (optional — shows a countdown on the public card when upcoming)</p>
              <input
                data-testid="activity-starts-at-input"
                type="date"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                className="w-full bg-[#0B1120] border border-white/10 text-slate-100 text-sm px-3 h-12 outline-none focus:border-[#ED1C24] transition-colors [color-scheme:dark]"
              />
            </div>
            <ImageUploadField testId="activity-image" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
            <GalleryField testId="activity-gallery" value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} />
          </div>
          <DialogFooter>
            <button
              data-testid="activity-save-btn"
              onClick={save}
              disabled={saving || !form.title}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : "Save Activity"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Promos() {
  const [promos, setPromos] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: 10, active: true, min_total: 0, usage_limit: 0 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => setPromos([]));
  }, []);
  useEffect(load, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: "", discount_type: "percent", value: 10, active: true, min_total: 0, usage_limit: 0 });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ code: p.code, discount_type: p.discount_type, value: p.value, active: p.active, min_total: p.min_total || 0, usage_limit: p.usage_limit || 0 });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, value: Number(form.value) || 0, min_total: Number(form.min_total) || 0, usage_limit: Number(form.usage_limit) || 0 };
      if (editing) {
        await api.put(`/admin/promos/${editing.id}`, payload);
        toast.success("Promo updated");
      } else {
        await api.post("/admin/promos", payload);
        toast.success("Promo created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo "${p.code}"?`)) return;
    try {
      await api.delete(`/admin/promos/${p.id}`);
      toast.success("Promo deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-promos-tab">
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-slate-400">{promos?.length ?? "…"} promo codes — customers apply them at checkout</p>
        <button
          data-testid="add-promo-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-5 py-2.5 transition-colors duration-300"
        >
          <Plus size={15} /> Add Promo Code
        </button>
      </div>

      <div className="bg-[#0F172A] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
              <th className="px-5 py-4">Code</th>
              <th className="px-5 py-4">Discount</th>
              <th className="px-5 py-4">Min Order</th>
              <th className="px-5 py-4">Used</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(promos || []).map((p) => (
              <tr key={p.id} data-testid={`admin-promo-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-3 font-display text-[#FFC61A] tracking-wider">{p.code}</td>
                <td className="px-5 py-3 text-slate-200">
                  {p.discount_type === "percent" ? `${p.value}% off` : `${usd(p.value)} off`}
                </td>
                <td className="px-5 py-3 text-slate-400">{p.min_total ? usd(p.min_total) : "—"}</td>
                <td className="px-5 py-3 text-slate-400">
                  {p.used_count || 0}{p.usage_limit ? ` / ${p.usage_limit}` : ""}
                </td>
                <td className="px-5 py-3">
                  {p.active ? (
                    <span className="text-emerald-400 text-xs uppercase tracking-wider">Active</span>
                  ) : (
                    <span className="text-slate-600 text-xs uppercase tracking-wider">Disabled</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`edit-promo-${p.id}`} onClick={() => openEdit(p)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#FFC61A] transition-colors" aria-label="Edit">
                      <Pencil size={13} />
                    </button>
                    <button data-testid={`delete-promo-${p.id}`} onClick={() => remove(p)} className="w-8 h-8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-[#ED1C24] transition-colors" aria-label="Delete">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {promos !== null && promos.length === 0 && (
          <p className="text-slate-500 text-sm text-center py-10">No promo codes yet.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="promo-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit Promo Code" : "Add Promo Code"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="promo-code-field" placeholder="Code (e.g. LAUNCH10)" value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={`${field} uppercase`} />
            <div className="grid grid-cols-2 gap-4">
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger data-testid="promo-type-select" className={`${field} w-full`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                  <SelectItem value="percent">Percent (%)</SelectItem>
                  <SelectItem value="fixed">Fixed amount ($)</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="promo-value-input" type="number" min="0" step="0.01"
                placeholder={form.discount_type === "percent" ? "Percent off" : "Amount off (USD)"}
                value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={field} />
              <Input data-testid="promo-min-input" type="number" min="0" step="0.01" placeholder="Min order (USD, 0 = none)"
                value={form.min_total} onChange={(e) => setForm({ ...form, min_total: e.target.value })} className={field} />
              <Input data-testid="promo-limit-input" type="number" min="0" placeholder="Usage limit (0 = unlimited)"
                value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className={field} />
            </div>
            <label className="flex items-center gap-3 text-sm text-slate-300">
              <Switch data-testid="promo-active-switch" checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })} />
              Active
            </label>
          </div>
          <DialogFooter>
            <button
              data-testid="promo-save-btn"
              onClick={save}
              disabled={saving || !form.code || !form.value}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : "Save Promo"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SIGNUP_STATUSES = ["new", "contacted", "registered", "completed", "closed"];

function Signups() {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/training-signups").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (s, status) => {
    try {
      await api.patch(`/admin/training-signups/${s.id}`, { status });
      toast.success(`Signup ${s.signup_number} → ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-signups-tab" className="bg-[#0F172A] border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
            <th className="px-5 py-4">Ref</th>
            <th className="px-5 py-4">Activity</th>
            <th className="px-5 py-4">Participant</th>
            <th className="px-5 py-4">Seats</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Date</th>
            <th className="px-5 py-4">Certificate</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((s) => (
            <tr key={s.id} data-testid={`admin-signup-row-${s.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="px-5 py-3 text-[#FFC61A] font-medium whitespace-nowrap">{s.signup_number}</td>
              <td className="px-5 py-3 text-slate-300 max-w-xs"><p className="line-clamp-1">{s.activity_title}</p></td>
              <td className="px-5 py-3">
                <p className="text-slate-200">{s.name}</p>
                <p className="text-xs text-slate-500">{s.email}{s.phone ? ` · ${s.phone}` : ""}</p>
                {s.company && <p className="text-xs text-slate-600">{s.company}</p>}
              </td>
              <td className="px-5 py-3 text-slate-400">{s.participants}</td>
              <td className="px-5 py-3">
                <Select value={s.status} onValueChange={(v) => setStatus(s, v)}>
                  <SelectTrigger data-testid={`signup-status-${s.id}`} className="bg-[#0B1120] border-white/10 text-slate-200 rounded-none h-9 w-32 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                    {SIGNUP_STATUSES.map((v) => (
                      <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{new Date(s.created_at).toLocaleDateString()}</td>
              <td className="px-5 py-3">
                {s.status === "completed" ? (
                  <a
                    data-testid={`signup-cert-btn-${s.id}`}
                    href={`${API}/certificates/${s.signup_number}?email=${encodeURIComponent(s.email)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[#FFC61A] hover:text-white border border-white/10 hover:border-[#FFC61A] px-3 py-2 transition-colors duration-200"
                  >
                    <Award size={13} /> PDF
                  </a>
                ) : (
                  <span data-testid={`signup-cert-na-${s.id}`} className="text-xs text-slate-600">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items !== null && items.length === 0 && (
        <p data-testid="no-signups-message" className="text-slate-500 text-sm text-center py-10">
          No training registrations yet.
        </p>
      )}
    </div>
  );
}


const REQUEST_STATUSES = ["pending", "in_review", "approved", "rejected"];

function PartnerRequestRow({ q, onSave, onDelete }) {
  const [status, setStatus] = useState(q.status);
  const [notes, setNotes] = useState(q.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = status !== q.status || notes !== (q.admin_notes || "");

  const save = async () => {
    setSaving(true);
    await onSave(q, status, notes);
    setSaving(false);
  };

  return (
    <tr data-testid={`admin-request-row-${q.id}`} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
      <td className="px-5 py-3 text-[#FFC61A] font-medium whitespace-nowrap">{q.req_number}</td>
      <td className="px-5 py-3">
        <p className="text-slate-200">{q.company}</p>
        <p className="text-xs text-slate-500">{q.contact_name} · {q.email}</p>
      </td>
      <td className="px-5 py-3 max-w-xs">
        <p className="text-slate-200 text-sm">{q.title}</p>
        {q.description && <p className="text-xs text-slate-500 mt-1 line-clamp-3">{q.description}</p>}
        {q.attachment && (
          <a
            data-testid={`request-attachment-${q.id}`}
            href={imgUrl(q.attachment)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 mt-1.5"
          >
            <ExternalLink size={11} /> Attachment
          </a>
        )}
      </td>
      <td className="px-5 py-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`request-status-${q.id}`} className="bg-[#0B1120] border-white/10 text-slate-200 rounded-none h-9 w-32 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
            {REQUEST_STATUSES.map((v) => (
              <SelectItem key={v} value={v} data-testid={`request-status-opt-${v}`} className="capitalize">
                {v.replace("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-5 py-3 min-w-56">
        <Input
          data-testid={`request-notes-${q.id}`}
          placeholder="Optional note to partner..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={field + " h-9 text-xs"}
        />
      </td>
      <td className="px-5 py-3">
        <div className="flex gap-2">
          <button
            data-testid={`request-save-${q.id}`}
            onClick={save}
            disabled={!dirty || saving}
            className="text-xs border border-white/15 text-slate-300 hover:border-[#ED1C24] hover:text-white disabled:opacity-40 px-4 py-2 transition-colors duration-200"
          >
            {saving ? "..." : "Save"}
          </button>
          <button
            data-testid={`request-delete-${q.id}`}
            onClick={() => onDelete(q)}
            className="text-xs text-slate-500 hover:text-[#ED1C24] border border-white/10 hover:border-[#ED1C24] px-3 py-2 transition-colors duration-200"
          >
            Delete
          </button>
        </div>
      </td>
      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{new Date(q.created_at).toLocaleDateString()}</td>
    </tr>
  );
}

function PartnerRequests() {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/partner-requests").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const save = async (q, status, notes) => {
    try {
      await api.patch(`/admin/partner-requests/${q.id}`, { status, admin_notes: notes });
      toast.success(`Request ${q.req_number} → ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete request ${q.req_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/partner-requests/${q.id}`);
      toast.success(`Request ${q.req_number} deleted`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-partner-requests-tab" className="bg-[#0F172A] border border-white/10 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
            <th className="px-5 py-4">Ref</th>
            <th className="px-5 py-4">Partner</th>
            <th className="px-5 py-4">Request</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Notes</th>
            <th className="px-5 py-4"></th>
            <th className="px-5 py-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((q) => (
            <PartnerRequestRow key={q.id} q={q} onSave={save} onDelete={remove} />
          ))}
        </tbody>
      </table>
      {items !== null && items.length === 0 && (
        <p data-testid="no-requests-message" className="text-slate-500 text-sm text-center py-10">
          No partner requests yet.
        </p>
      )}
    </div>
  );
}

const PARTNER_STATUSES = ["new", "contacted", "approved", "rejected"];
const PARTNER_TYPES = ["Business Partner", "Supply Partner", "IT Support Technical Partner", "Reseller Partner", "Community / NGO Partner"];

function Partners() {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/partner-applications").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (a, status, partnerType) => {
    try {
      await api.patch(`/admin/partner-applications/${a.id}`, { status, partner_type: partnerType ?? a.partner_type ?? "" });
      toast.success(
        status === "approved"
          ? `${a.company} approved — added to Clients`
          : `Application ${a.app_number} → ${status}`
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setType = async (a, partnerType) => {
    try {
      await api.patch(`/admin/partner-applications/${a.id}`, { status: a.status, partner_type: partnerType });
      toast.success(`${a.company} type → ${partnerType}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete application ${a.app_number} (${a.company})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/partner-applications/${a.id}`);
      toast.success(`Application ${a.app_number} deleted`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setRate = async (a, raw) => {
    if (raw === "") return;
    const n = Math.max(0, Math.min(Number(raw) || 0, 90));
    try {
      await api.patch(`/admin/partner-applications/${a.id}`, {
        status: a.status, partner_type: a.partner_type || "", discount_pct: n,
      });
      toast.success(`${a.company} partner rate → ${n}%`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resetRate = async (a) => {
    try {
      await api.patch(`/admin/partner-applications/${a.id}`, {
        status: a.status, partner_type: a.partner_type || "", discount_pct: -1,
      });
      toast.success(`${a.company} back to global rate`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-partners-tab" className="bg-[#0F172A] border border-white/10 overflow-x-auto">
      <p className="text-xs text-slate-500 px-5 pt-4">
        Approving an application adds the organization to the public Clients section.
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
            <th className="px-5 py-4">Ref</th>
            <th className="px-5 py-4">Company</th>
            <th className="px-5 py-4">Contact</th>
            <th className="px-5 py-4">Type</th>
            <th className="px-5 py-4">Message</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Partner Type</th>
            <th className="px-5 py-4">Certificate</th>
            <th className="px-5 py-4"></th>
            <th className="px-5 py-4">Date</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((a) => (
            <tr key={a.id} data-testid={`admin-partner-row-${a.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="px-5 py-3 text-[#FFC61A] font-medium whitespace-nowrap">{a.app_number}</td>
              <td className="px-5 py-3 text-slate-200">{a.company}</td>
              <td className="px-5 py-3">
                <p className="text-slate-200">{a.contact_name}</p>
                <p className="text-xs text-slate-500">{a.email}{a.phone ? ` · ${a.phone}` : ""}</p>
              </td>
              <td className="px-5 py-3 text-slate-400 text-xs">{a.business_type || "—"}</td>
              <td className="px-5 py-3 text-slate-400 max-w-xs"><p className="line-clamp-2 text-xs">{a.message || "—"}</p></td>
              <td className="px-5 py-3">
                <Select value={a.status} onValueChange={(v) => setStatus(a, v)}>
                  <SelectTrigger data-testid={`partner-status-${a.id}`} className="bg-[#0B1120] border-white/10 text-slate-200 rounded-none h-9 w-32 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                    {PARTNER_STATUSES.map((v) => (
                      <SelectItem key={v} value={v} data-testid={`partner-status-opt-${v}`} className="capitalize">{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              <td className="px-5 py-3">
                <Select value={a.partner_type || "Business Partner"} onValueChange={(v) => setType(a, v)}>
                  <SelectTrigger data-testid={`partner-type-${a.id}`} className="bg-[#0B1120] border-white/10 text-slate-200 rounded-none h-9 w-48 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                    {PARTNER_TYPES.map((v) => (
                      <SelectItem key={v} value={v} data-testid={`partner-type-opt-${v}`}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1.5 mt-2">
                  <Input
                    data-testid={`partner-rate-${a.id}`}
                    type="number" min="0" max="90"
                    placeholder="Global"
                    defaultValue={a.discount_pct ?? ""}
                    onBlur={(e) => setRate(a, e.target.value)}
                    className="bg-[#0B1120] border-white/10 text-slate-200 rounded-none h-8 w-20 text-xs"
                  />
                  <span className="text-[10px] text-slate-500">% off</span>
                  {a.discount_pct != null && (
                    <button
                      data-testid={`partner-rate-reset-${a.id}`}
                      onClick={() => resetRate(a)}
                      title="Reset to global rate"
                      className="text-slate-500 hover:text-[#ED1C24] text-sm px-1 transition-colors duration-200"
                    >
                      ×
                    </button>
                  )}
                </div>
              </td>
              <td className="px-5 py-3">
                {a.status === "approved" ? (
                  <a
                    data-testid={`partner-cert-btn-${a.id}`}
                    href={`${API}/partner-certificates/${a.app_number}?email=${encodeURIComponent(a.email)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-[#FFC61A] hover:text-white border border-white/10 hover:border-[#FFC61A] px-3 py-2 transition-colors duration-200"
                  >
                    <Award size={13} /> PDF
                  </a>
                ) : (
                  <span data-testid={`partner-cert-na-${a.id}`} className="text-xs text-slate-600">—</span>
                )}
              </td>
              <td className="px-5 py-3">
                <button
                  data-testid={`partner-delete-${a.id}`}
                  onClick={() => remove(a)}
                  className="text-xs text-slate-500 hover:text-[#ED1C24] border border-white/10 hover:border-[#ED1C24] px-3 py-2 transition-colors duration-200"
                >
                  Delete
                </button>
              </td>
              <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{new Date(a.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items !== null && items.length === 0 && (
        <p data-testid="no-partners-message" className="text-slate-500 text-sm text-center py-10">
          No partner applications yet.
        </p>
      )}
    </div>
  );
}

const EMAIL_STATUS_STYLE = {
  sent: "text-emerald-400 border-emerald-400/40",
  failed: "text-[#ED1C24] border-[#ED1C24]/40",
  skipped: "text-slate-400 border-white/20",
};

function EmailLog() {
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/email-log").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  return (
    <div data-testid="admin-emails-tab" className="bg-[#0F172A] border border-white/10 overflow-x-auto">
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <p className="text-xs text-slate-500">Latest 100 email attempts — orders, licenses, tickets, signups and partner messages.</p>
        <button data-testid="emails-refresh-btn" onClick={load}
          className="text-xs text-slate-300 hover:text-[#FFC61A] border border-white/15 hover:border-[#FFC61A] px-4 py-2 transition-colors duration-200">
          Refresh
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-slate-500 border-b border-white/10">
            <th className="px-5 py-4">Time</th>
            <th className="px-5 py-4">To</th>
            <th className="px-5 py-4">Subject</th>
            <th className="px-5 py-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((e) => (
            <tr key={e.id} data-testid={`email-row-${e.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
              <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                {new Date(e.created_at).toLocaleString()}
              </td>
              <td className="px-5 py-3 text-slate-300 text-xs">{e.to}</td>
              <td className="px-5 py-3 max-w-md">
                <p className="text-slate-200 text-xs truncate">{e.subject}</p>
                {e.status === "failed" && e.detail && (
                  <p className="text-[10px] text-[#ED1C24] mt-0.5 truncate">{e.detail}</p>
                )}
              </td>
              <td className="px-5 py-3">
                <span data-testid={`email-status-${e.id}`} className={`text-[10px] tracking-[0.15em] uppercase border px-2.5 py-1 ${EMAIL_STATUS_STYLE[e.status] || EMAIL_STATUS_STYLE.skipped}`}>
                  {e.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {items !== null && items.length === 0 && (
        <p data-testid="no-emails-message" className="text-slate-500 text-sm text-center py-10">
          No emails logged yet.
        </p>
      )}
    </div>
  );
}

const SECTION_LABELS = {
  hero: "Hero Banner",
  marquee: "Marquee Strip",
  about: "Who We Are",
  why: "Why Choose KINOS",
  services: "Services",
  featured: "Featured Products",
  kit_cta: "Kit Builder Banner",
  brands: "Brands",
  bitdefender: "Bitdefender",
  projects: "Projects",
  activities: "Activities & Training",
  team: "Team",
  testimonials: "Testimonials",
  clients: "Clients",
  contact: "Contact",
};
const DEFAULT_SECTION_ORDER = Object.keys(SECTION_LABELS);

function SettingsTab() {
  const [pct, setPct] = useState("10");
  const [teamEmail, setTeamEmail] = useState("");
  const [catRates, setCatRates] = useState({});
  const [categories, setCategories] = useState([]);
  const [secOrder, setSecOrder] = useState(DEFAULT_SECTION_ORDER);
  const [logo, setLogo] = useState("");
  const [reminderDays, setReminderDays] = useState("3");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/settings"),
      api.get("/admin/products"),
    ]).then(([s, p]) => {
      setPct(s.data?.partner_discount || "10");
      setTeamEmail(s.data?.team_email || "");
      setLogo(s.data?.site_logo || "");
      setReminderDays(s.data?.kit_reminder_days || "3");
      try {
        setCatRates(JSON.parse(s.data?.category_discounts || "{}"));
      } catch { setCatRates({}); }
      try {
        const saved = JSON.parse(s.data?.homepage_sections || "[]");
        const valid = saved.filter((x) => SECTION_LABELS[x]);
        const merged = [...valid];
        for (const m of DEFAULT_SECTION_ORDER.filter((x) => !valid.includes(x))) {
          let inserted = false;
          for (let p = DEFAULT_SECTION_ORDER.indexOf(m) - 1; p >= 0; p--) {
            const at = merged.indexOf(DEFAULT_SECTION_ORDER[p]);
            if (at >= 0) { merged.splice(at + 1, 0, m); inserted = true; break; }
          }
          if (!inserted) merged.unshift(m);
        }
        setSecOrder(merged.length ? merged : DEFAULT_SECTION_ORDER);
      } catch { /* keep default */ }
      setCategories([...new Set(p.data.map((x) => x.category))].sort());
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const moveSection = (key, dir) => {
    setSecOrder((prev) => {
      const i = prev.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = async () => {
    const n = Number(pct);
    if (!Number.isFinite(n) || n < 0 || n > 90) {
      toast.error("Discount must be between 0 and 90%");
      return;
    }
    if (teamEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teamEmail)) {
      toast.error("Team email looks invalid");
      return;
    }
    const cleanRates = {};
    for (const [k, v] of Object.entries(catRates)) {
      if (v !== "" && v != null) {
        const cn = Math.max(0, Math.min(Number(v) || 0, 90));
        cleanRates[k] = cn;
      }
    }
    setSaving(true);
    try {
      await api.post("/admin/settings", { key: "partner_discount", value: String(Math.round(n)) });
      await api.post("/admin/settings", { key: "team_email", value: teamEmail.trim() });
      await api.post("/admin/settings", { key: "category_discounts", value: JSON.stringify(cleanRates) });
      await api.post("/admin/settings", { key: "homepage_sections", value: JSON.stringify(secOrder) });
      await api.post("/admin/settings", { key: "site_logo", value: logo });
      const rd = parseInt(reminderDays, 10);
      if (!isNaN(rd) && rd >= 1 && rd <= 30) {
        await api.post("/admin/settings", { key: "kit_reminder_days", value: String(rd) });
      } else {
        toast.error("Kit reminder days must be between 1 and 30");
        setSaving(false);
        return;
      }
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="h-40 bg-white/[0.03] animate-pulse" />;

  return (
    <div data-testid="admin-settings-tab" className="max-w-xl">
      <div className="bg-[#0F172A] border border-white/10 p-8 space-y-5">
        <p className="text-sm text-slate-400">
          Partner pricing — the discount approved partners receive on retail prices in their downloadable price list.
        </p>
        <div>
          <p className="text-[11px] text-slate-500 mb-1.5">Partner discount (%)</p>
          <Input data-testid="settings-discount-input" type="number" min="0" max="90" value={pct}
            onChange={(e) => setPct(e.target.value)} className={field} />
          <p className="text-[11px] text-slate-500 mt-1.5">
            Example: at {pct || 0}%, a $100.00 product appears in the partner price list at ${(100 * (1 - (Number(pct) || 0) / 100)).toFixed(2)}.
          </p>
        </div>
        <div className="border-t border-white/10 pt-5">
          <p className="text-sm text-slate-400 mb-3">
            Per-category rates (optional) — override the global rate for specific categories. A partner&apos;s own rate always wins.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {categories.map((c) => (
              <div key={c}>
                <p className="text-[11px] text-slate-500 mb-1 truncate">{c}</p>
                <Input
                  data-testid={`settings-cat-${c}`}
                  type="number" min="0" max="90" placeholder={pct}
                  value={catRates[c] ?? ""}
                  onChange={(e) => setCatRates({ ...catRates, [c]: e.target.value })}
                  className={field + " h-9 text-xs"}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-white/10 pt-5">
          <p className="text-sm text-slate-400 mb-3">
            Team inbox — where order alerts, tickets, signups and partner notifications are sent.
          </p>
          <p className="text-[11px] text-slate-500 mb-1.5">Team notification email</p>
          <Input data-testid="settings-team-email-input" type="email" placeholder="e.g. service@kinos.tl" value={teamEmail}
            onChange={(e) => setTeamEmail(e.target.value)} className={field} />
        </div>
        <div className="border-t border-white/10 pt-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-400">
              Homepage section order — use the arrows to rearrange, then Save Settings.
            </p>
            <button
              data-testid="settings-sections-reset-btn"
              onClick={() => setSecOrder(DEFAULT_SECTION_ORDER)}
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-white transition-colors"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>
          <div className="space-y-1.5">
            {secOrder.map((key, i) => (
              <div
                key={key}
                data-testid={`settings-section-${key}`}
                className="flex items-center gap-3 bg-white/[0.03] border border-white/10 px-3 py-2"
              >
                <span className="text-[11px] text-slate-500 w-5 text-right">{i + 1}</span>
                <span className="flex-1 text-sm text-slate-200">{SECTION_LABELS[key]}</span>
                <button
                  data-testid={`settings-section-${key}-up`}
                  onClick={() => moveSection(key, -1)}
                  disabled={i === 0}
                  className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                  aria-label={`Move ${SECTION_LABELS[key]} up`}
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  data-testid={`settings-section-${key}-down`}
                  onClick={() => moveSection(key, 1)}
                  disabled={i === secOrder.length - 1}
                  className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
                  aria-label={`Move ${SECTION_LABELS[key]} down`}
                >
                  <ArrowDown size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-white/10 pt-5">
          <p className="text-sm text-slate-400 mb-3">
            Kit reminder — days after a kit link is emailed before the customer gets an automatic reminder (1–30).
          </p>
          <Input
            data-testid="settings-kit-reminder-days"
            type="number"
            min="1"
            max="30"
            value={reminderDays}
            onChange={(e) => setReminderDays(e.target.value)}
            className="bg-[#0B1120] border-white/10 text-slate-100 rounded-none h-11 w-32"
          />
        </div>
        <div className="border-t border-white/10 pt-5">
          <p className="text-sm text-slate-400 mb-3">
            Site logo — shown in the header. A default KINOS logo is bundled with the site; upload here to replace it.
          </p>
          <ImageUploadField testId="settings-site-logo" value={logo} onChange={setLogo} />
        </div>
        <BackupSection />
        <button
          data-testid="settings-save-btn"
          onClick={save}
          disabled={saving}
          className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}


const EMPTY_BANNER = { active: false, message: "", link: "", link_label: "", code: "", starts_at: "", ends_at: "" };

function Announcement() {
  const [banners, setBanners] = useState([]);
  const [clicks, setClicks] = useState({});
  const [editing, setEditing] = useState(null); // index or -1 for new
  const [form, setForm] = useState(EMPTY_BANNER);
  const [promos, setPromos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      let list = [];
      try {
        list = JSON.parse(r.data?.announcements || "[]");
      } catch { list = []; }
      if (!list.length && r.data?.announcement) {
        try {
          const legacy = JSON.parse(r.data.announcement);
          if (legacy?.message) list = [legacy];
        } catch { /* ignore */ }
      }
      setBanners(list);
      setLoaded(true);
    }).catch(() => setLoaded(true));
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/admin/banner-clicks").then((r) => {
      const map = {};
      r.data.forEach((c) => { map[c.message] = c.clicks; });
      setClicks(map);
    }).catch(() => {});
  }, []);

  const persist = async (list, msg) => {
    setSaving(true);
    try {
      await api.post("/admin/settings", { key: "announcements", value: JSON.stringify(list) });
      setBanners(list);
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveForm = async () => {
    const list = [...banners];
    if (editing === -1) list.push(form);
    else list[editing] = form;
    await persist(list, editing === -1 ? "Banner added" : "Banner updated");
    setEditing(null);
    setForm(EMPTY_BANNER);
  };

  const toggleActive = async (i) => {
    const list = banners.map((b, j) => (j === i ? { ...b, active: !b.active } : b));
    await persist(list, list[i].active ? "Banner live" : "Banner paused");
  };

  const remove = async (i) => {
    if (!window.confirm(`Delete banner "${banners[i].message.slice(0, 40)}"?`)) return;
    await persist(banners.filter((_, j) => j !== i), "Banner deleted");
  };

  const bannerStatus = (b) => {
    const now = new Date();
    if (!b.active) return { label: "paused", cls: "text-slate-400 border-white/20" };
    if (b.starts_at && new Date(b.starts_at) > now) return { label: "scheduled", cls: "text-sky-400 border-sky-400/40" };
    if (b.ends_at && new Date(b.ends_at) < now) return { label: "expired", cls: "text-slate-500 border-white/10" };
    return { label: "live now", cls: "text-emerald-400 border-emerald-400/40" };
  };

  if (!loaded) return <div className="h-40 bg-white/[0.03] animate-pulse" />;

  return (
    <div data-testid="admin-announcement-tab" className="max-w-3xl space-y-6">
      <div className="bg-[#0F172A] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-400">
            Scheduled banners — the first live, in-window banner shows at the top of the website.
          </p>
          <button
            data-testid="ann-new-btn"
            onClick={() => { setForm(EMPTY_BANNER); setEditing(-1); }}
            className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-xs font-medium px-4 py-2.5 transition-colors duration-300"
          >
            <Plus size={14} /> New banner
          </button>
        </div>
        {banners.length === 0 ? (
          <p data-testid="no-banners-message" className="text-slate-500 text-sm py-4">No banners yet — create one.</p>
        ) : (
          <div className="space-y-3">
            {banners.map((b, i) => {
              const st = bannerStatus(b);
              return (
                <div key={i} data-testid={`banner-row-${i}`} className="flex flex-wrap items-center gap-3 border border-white/10 px-4 py-3">
                  <span className={`text-[10px] tracking-[0.15em] uppercase border px-2 py-1 shrink-0 ${st.cls}`}>{st.label}</span>
                  <p className="text-sm text-slate-200 flex-1 min-w-40 truncate">{b.message}</p>
                  <p className="text-[11px] text-slate-500 shrink-0">
                    {b.starts_at ? new Date(b.starts_at).toLocaleDateString() : "now"} → {b.ends_at ? new Date(b.ends_at).toLocaleDateString() : "open"}
                  </p>
                  <span data-testid={`banner-clicks-${i}`} className="text-[11px] text-[#FFC61A] shrink-0" title="CTA clicks">
                    {clicks[b.message] || 0} clicks
                  </span>
                  <button data-testid={`banner-toggle-${i}`} onClick={() => toggleActive(i)}
                    className="text-xs text-slate-300 hover:text-[#FFC61A] border border-white/15 px-3 py-1.5 transition-colors duration-200">
                    {b.active ? "Pause" : "Activate"}
                  </button>
                  <button data-testid={`banner-edit-${i}`} onClick={() => { setForm({ ...EMPTY_BANNER, ...b }); setEditing(i); }}
                    className="text-xs text-slate-300 hover:text-white border border-white/15 px-3 py-1.5 transition-colors duration-200">
                    Edit
                  </button>
                  <button data-testid={`banner-delete-${i}`} onClick={() => remove(i)}
                    className="text-xs text-slate-500 hover:text-[#ED1C24] border border-white/10 px-3 py-1.5 transition-colors duration-200">
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing !== null && (
        <div data-testid="banner-form" className="bg-[#0F172A] border border-white/10 p-8 space-y-5">
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24]">{editing === -1 ? "New banner" : "Edit banner"}</p>
          <div>
            <Input data-testid="ann-message-input" placeholder="Message (e.g. Launch promo: 10% off all hardware this month)" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} className={field} />
            <div className="flex justify-end mt-1.5">
              <AiGenerateButton testId="ann-ai-btn" kind="announcement_message" name={form.message || "KINOS promotion"} context={form.link_label}
                onDone={(text) => setForm((f) => ({ ...f, message: text }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input data-testid="ann-link-input" placeholder="Link URL (e.g. /shop)" value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })} className={field} />
            <Input data-testid="ann-link-label-input" placeholder="Link label (e.g. Shop now)" value={form.link_label}
              onChange={(e) => setForm({ ...form, link_label: e.target.value })} className={field} />
          </div>
          <Select value={form.code || "none"} onValueChange={(v) => setForm({ ...form, code: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="ann-code-select" className={`${field} w-full`}>
              <SelectValue placeholder="Promo code to display (optional)" />
            </SelectTrigger>
            <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
              <SelectItem value="none">No promo code</SelectItem>
              {promos.filter((p) => p.active).map((p) => (
                <SelectItem key={p.id} value={p.code} data-testid={`ann-code-${p.code}`}>{p.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <Switch data-testid="ann-active-switch" checked={form.active}
              onCheckedChange={(v) => setForm({ ...form, active: v })} />
            Live on the website
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[11px] text-slate-500 mb-1.5">Start showing (optional)</p>
              <Input data-testid="ann-starts-input" type="datetime-local" value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={field} />
            </div>
            <div>
              <p className="text-[11px] text-slate-500 mb-1.5">Stop showing (optional)</p>
              <Input data-testid="ann-ends-input" type="datetime-local" value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className={field} />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 -mt-1">Outside this window the banner hides itself automatically, even when Live is on.</p>
          <div className="flex gap-3">
            <button
              data-testid="ann-save-btn"
              onClick={saveForm}
              disabled={saving || !form.message}
              className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
            >
              {saving ? "Saving..." : editing === -1 ? "Add Banner" : "Save Banner"}
            </button>
            <button
              data-testid="ann-cancel-btn"
              onClick={() => { setEditing(null); setForm(EMPTY_BANNER); }}
              className="text-sm text-slate-300 hover:text-white border border-white/15 px-6 py-2.5 transition-colors duration-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AiGenerateButton({ testId, kind, name, context, onDone }) {
  const [busy, setBusy] = useState(false);
  const [aiLang, setAiLang] = useState("en");
  const generate = async () => {
    if (!name?.trim()) {
      toast.error("Enter a name/title first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai-text", { kind, name: name.trim(), context: (context || "").trim(), lang: aiLang });
      onDone(data.text);
      toast.success("AI text generated — review and edit as needed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        data-testid={testId}
        onClick={generate}
        disabled={busy}
        className="inline-flex items-center gap-1.5 text-[11px] text-[#FFC61A] hover:text-white border border-[#FFC61A]/30 hover:border-[#FFC61A] px-2.5 py-1.5 transition-colors duration-300 disabled:opacity-50"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
        {busy ? "Generating..." : "Generate with AI"}
      </button>
      <button
        type="button"
        data-testid={`${testId}-lang`}
        onClick={() => setAiLang(aiLang === "en" ? "tet" : "en")}
        title="AI language"
        className={`text-[10px] font-semibold tracking-wide border px-2 py-1.5 transition-colors duration-300 ${
          aiLang === "tet"
            ? "border-[#ED1C24] text-[#ED1C24] bg-[#ED1C24]/10"
            : "border-white/15 text-slate-500 hover:text-white"
        }`}
      >
        {aiLang === "tet" ? "TET" : "EN"}
      </button>
    </span>
  );
}

function UserManager() {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "editor" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.email.trim() || !form.password) { toast.error("Email and password are required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "editor" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!users) return <div className="h-40 bg-white/[0.03] animate-pulse" />;

  return (
    <div data-testid="admin-users-tab" className="max-w-3xl">
      <div className="bg-[#0F172A] border border-white/10 p-8 space-y-5">
        <p className="text-sm text-slate-400">
          Admin can manage everything. Editor can update the website and products, but cannot change settings, users or backups.
        </p>
        <div className="border border-white/10 divide-y divide-white/10">
          {users.map((u, i) => (
            <div key={u.id} data-testid={`user-row-${i}`} className="flex items-center gap-4 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{u.name} <span className="text-slate-500 font-normal">· {u.email}</span></p>
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 border ${
                u.role === "admin" ? "text-[#FFC61A] border-[#FFC61A]/40 bg-[#FFC61A]/10" : "text-slate-400 border-white/15"
              }`}>{u.role}</span>
              <button
                data-testid={`user-delete-${i}`}
                onClick={() => remove(u)}
                className="w-8 h-8 flex items-center justify-center text-slate-500 hover:text-[#ED1C24] transition-colors"
                aria-label={`Delete ${u.email}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 pt-5">
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">Add user</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="user-name-input" placeholder="Name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <Input data-testid="user-email-input" type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
            <Input data-testid="user-password-input" type="password" placeholder="Password (min 8 characters)" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-role-select" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                <SelectItem value="editor" data-testid="user-role-editor">Editor — website & products</SelectItem>
                <SelectItem value="admin" data-testid="user-role-admin">Admin — full access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid="user-add-btn"
            onClick={add}
            disabled={saving}
            className="mt-4 inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
          >
            <UserPlus size={14} /> {saving ? "Creating..." : "Create User"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BackupSection() {
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(data, null, 1)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kinos-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let data;
    try {
      data = JSON.parse(await file.text());
    } catch {
      toast.error("Invalid backup file (not JSON)");
      return;
    }
    if (!data.collections || typeof data.collections !== "object") {
      toast.error("Invalid backup file (missing collections)");
      return;
    }
    if (!window.confirm("Restore will REPLACE all current website content with the backup. Continue?")) return;
    setRestoring(true);
    try {
      const { data: res } = await api.post("/admin/restore", { collections: data.collections });
      toast.success(`Restored: ${Object.entries(res.restored).map(([k, v]) => `${k} (${v})`).join(", ")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="border-t border-white/10 pt-5">
      <p className="text-sm text-slate-400 mb-3">
        System backup — download all website content (products, projects, team, activities, orders…) as a file, or restore from a previous backup. User accounts are never included.
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          data-testid="backup-download-btn"
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FFC61A] text-slate-300 hover:text-[#FFC61A] disabled:opacity-50 text-sm px-5 py-2.5 transition-colors duration-300"
        >
          <ArrowDown size={14} /> {busy ? "Preparing..." : "Download Backup"}
        </button>
        <label
          data-testid="backup-restore-btn"
          className="inline-flex items-center gap-2 border border-[#ED1C24]/40 text-[#ED1C24] hover:bg-[#ED1C24] hover:text-white text-sm px-5 py-2.5 cursor-pointer transition-colors duration-300"
        >
          <ArrowUp size={14} /> {restoring ? "Restoring..." : "Restore from Backup"}
          <input data-testid="backup-restore-file" type="file" accept="application/json" className="hidden" disabled={restoring} onChange={restore} />
        </label>
      </div>
    </div>
  );
}

function ServiceStatusManager() {
  const [services, setServices] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/service-status").then((r) => setServices(r.data.services)).catch(() => setServices([]));
  }, []);

  const update = (i, patch) =>
    setServices((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const save = async () => {
    if (services.some((s) => !s.name.trim())) {
      toast.error("Every service needs a name");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/service-status", { services });
      setServices(data.services);
      toast.success("Service status saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!services) return <div className="h-40 bg-white/[0.03] animate-pulse" />;

  return (
    <div data-testid="admin-service-status-tab" className="max-w-3xl">
      <div className="bg-[#0F172A] border border-white/10 p-8 space-y-4">
        <p className="text-sm text-slate-400">
          Live status shown on the public Service Status page (/status). Changes appear for clients immediately after saving.
        </p>
        {services.map((s, i) => (
          <div key={i} data-testid={`svc-row-${i}`} className="flex flex-wrap items-center gap-3 border border-white/10 bg-white/[0.02] p-4">
            <Input
              data-testid={`svc-name-${i}`}
              value={s.name}
              onChange={(e) => update(i, { name: e.target.value })}
              className={field + " flex-1 min-w-[160px]"}
            />
            <Select value={s.status} onValueChange={(v) => update(i, { status: v })}>
              <SelectTrigger data-testid={`svc-status-${i}`} className={field + " w-40"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                <SelectItem value="operational" data-testid={`svc-status-${i}-operational`}>Operational</SelectItem>
                <SelectItem value="degraded" data-testid={`svc-status-${i}-degraded`}>Degraded</SelectItem>
                <SelectItem value="down" data-testid={`svc-status-${i}-down`}>Down</SelectItem>
              </SelectContent>
            </Select>
            <Input
              data-testid={`svc-note-${i}`}
              value={s.note || ""}
              placeholder="Note (optional, e.g. maintenance window)"
              onChange={(e) => update(i, { note: e.target.value })}
              className={field + " flex-1 min-w-[180px]"}
            />
            <button
              data-testid={`svc-remove-${i}`}
              onClick={() => setServices(services.filter((_, j) => j !== i))}
              className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-[#ED1C24] transition-colors"
              aria-label={`Remove ${s.name}`}
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            data-testid="svc-add-btn"
            onClick={() => setServices([...services, { name: "", status: "operational", note: "", updated_at: null }])}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-slate-300 hover:text-white text-sm px-5 py-2.5 transition-colors duration-300"
          >
            <Plus size={14} /> Add Service
          </button>
          <button
            data-testid="svc-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white text-sm font-medium px-6 py-2.5 transition-colors duration-300"
          >
            {saving ? "Saving..." : "Save Status"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectManager() {
  const [items, setItems] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", tag: "", location: "", image: "", description: "" });

  const load = () =>
    api.get("/projects").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", tag: "", location: "", image: "", description: "" });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, tag: p.tag || "", location: p.location || "", image: p.image || "", description: p.description || "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      if (editing) await api.put(`/admin/projects/${editing.id}`, form);
      else await api.post("/admin/projects", form);
      toast.success(editing ? "Project updated" : "Project added");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Project deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!items) return <div className="h-40 bg-white/[0.03] animate-pulse" />;

  return (
    <div data-testid="admin-projects-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-400">{items.length} projects shown on the homepage</p>
        <button
          data-testid="add-project-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-4 py-2 transition-colors duration-300"
        >
          <Plus size={14} /> Add Project
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((p, i) => (
          <div key={p.id} data-testid={`admin-project-card-${i}`} className="border border-white/10 bg-[#0F172A] overflow-hidden">
            {p.image && <img src={imgUrl(p.image)} alt={p.title} className="w-full h-32 object-cover" />}
            <div className="p-4">
              <p className="font-display font-semibold text-white text-base leading-snug">{p.title}</p>
              <p className="text-xs text-slate-500 mt-1">{p.tag}{p.location ? ` — ${p.location}` : ""}</p>
              <div className="flex gap-2 mt-3">
                <button
                  data-testid={`edit-project-${i}`}
                  onClick={() => openEdit(p)}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  data-testid={`delete-project-${i}`}
                  onClick={() => remove(p)}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#ED1C24] border border-white/10 hover:border-[#ED1C24]/50 px-3 py-1.5 transition-colors"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="project-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit project" : "Add project"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input data-testid="project-title-input" placeholder="Title (e.g. Government Network Modernization)" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} className={field} />
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="project-tag-input" placeholder="Tag (e.g. Networking)" value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })} className={field} />
              <Input data-testid="project-location-input" placeholder="Location (e.g. Dili)" value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })} className={field} />
            </div>
            <div>
              <Textarea data-testid="project-description-input" placeholder="Description (optional)" rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <div className="flex justify-end mt-1.5">
                <AiGenerateButton testId="project-ai-btn" kind="project_description" name={form.title} context={form.tag}
                  onDone={(text) => setForm((f) => ({ ...f, description: text }))} />
              </div>
            </div>
            <div>
              <p className="text-[11px] text-slate-500 mb-1.5">Project photo</p>
              <ImageUploadField testId="project-image" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
            </div>
            <button
              data-testid="project-save-btn"
              onClick={save}
              className="w-full bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium py-3 transition-colors duration-300"
            >
              {editing ? "Save changes" : "Add project"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (form.next !== form.confirm) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Password changed");
      setForm({ current: "", next: "", confirm: "" });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="change-password-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Change password</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <Input data-testid="cp-current-input" type="password" placeholder="Current password" value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })} className={field} />
          <Input data-testid="cp-new-input" type="password" placeholder="New password (min 8 characters)" value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })} className={field} />
          <Input data-testid="cp-confirm-input" type="password" placeholder="Confirm new password" value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={field} />
          <button
            data-testid="cp-save-btn"
            onClick={submit}
            disabled={saving || !form.current || !form.next}
            className="w-full bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-3 transition-colors duration-300"
          >
            {saving ? "Saving..." : "Change password"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AppsManager() {
  const [items, setItems] = useState(null);
  const [interests, setInterests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", tagline: "", description: "", tag: "", url: "", image: "", status: "live", progress: 0, gallery: [] });

  const load = () => {
    api.get("/apps").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/app-interest").then((r) => setInterests(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const interestCount = (id) => interests.filter((x) => x.app_id === id).length;

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", tagline: "", description: "", tag: "", url: "", image: "", status: "live", progress: 0, gallery: [] });
    setDialogOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ name: a.name, tagline: a.tagline || "", description: a.description || "", tag: a.tag || "", url: a.url || "", image: a.image || "", status: a.status || "live", progress: a.progress || 0, gallery: a.gallery || [] });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editing) await api.put(`/admin/apps/${editing.id}`, form);
      else await api.post("/admin/apps", form);
      toast.success(editing ? "App updated" : "App added");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete app "${a.name}"?`)) return;
    try {
      await api.delete(`/admin/apps/${a.id}`);
      toast.success("App deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!items) return <div className="h-40 bg-white/[0.03] animate-pulse" />;

  return (
    <div data-testid="admin-apps-tab">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-slate-400">{items.length} commercial apps shown on the public /apps page</p>
        <button
          data-testid="add-app-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-medium px-4 py-2 transition-colors duration-300"
        >
          <Plus size={14} /> Add App
        </button>
      </div>
      {items.length === 0 && (
        <p className="text-sm text-slate-500 border border-dashed border-white/15 p-8 text-center">
          No apps yet — add your first commercial SaaS app with its link and it appears on the public page instantly.
        </p>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((a, i) => (
          <div key={a.id} data-testid={`admin-app-card-${i}`} className="border border-white/10 bg-[#0F172A] overflow-hidden">
            {a.image && <img src={imgUrl(a.image)} alt={a.name} className="w-full h-28 object-cover" />}
            <div className="p-4">
              <div className="flex items-center gap-2">
                <p className="font-display font-semibold text-white text-base leading-snug flex-1">{a.name}</p>
                {interestCount(a.id) > 0 && (
                  <span data-testid={`app-interest-count-${i}`} className="text-[10px] uppercase tracking-wide px-2 py-0.5 border border-[#FFC61A]/40 text-[#FFC61A]">
                    {interestCount(a.id)} interested
                  </span>
                )}
                <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${{
                  live: "text-emerald-400 border-emerald-400/40",
                  coming_soon: "text-amber-400 border-amber-400/40",
                  developing: "text-sky-400 border-sky-400/40",
                  ready: "text-violet-400 border-violet-400/40",
                }[a.status] || "text-amber-400 border-amber-400/40"}`}>{{
                  live: "Live", coming_soon: "Soon", developing: "In dev", ready: "Ready",
                }[a.status] || a.status}</span>
              </div>
              {a.url && (
                <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-[#FFC61A] hover:underline break-all">{a.url}</a>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  data-testid={`edit-app-${i}`}
                  onClick={() => openEdit(a)}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  data-testid={`delete-app-${i}`}
                  onClick={() => remove(a)}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#ED1C24] border border-white/10 hover:border-[#ED1C24]/50 px-3 py-1.5 transition-colors"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="app-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing ? "Edit app" : "Add app"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input data-testid="app-name-input" placeholder="App name (e.g. KINOS HRMS)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
            <Input data-testid="app-tagline-input" placeholder="Tagline (e.g. Payroll & HR for Timor-Leste)" value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={field} />
            <div>
              <Textarea data-testid="app-description-input" placeholder="Short description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none" />
              <div className="flex justify-end mt-1.5">
                <AiGenerateButton testId="app-ai-btn" kind="project_description" name={form.name} context={form.tagline}
                  onDone={(text) => setForm((f) => ({ ...f, description: text }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="app-tag-input" placeholder="Tag (e.g. HR, Inventory)" value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })} className={field} />
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="app-status-select" className={field}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none">
                  <SelectItem value="live" data-testid="app-status-live">Live</SelectItem>
                  <SelectItem value="coming_soon" data-testid="app-status-coming">Coming soon</SelectItem>
                  <SelectItem value="developing" data-testid="app-status-developing">In development</SelectItem>
                  <SelectItem value="ready" data-testid="app-status-ready">Ready — awaiting deployment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.status === "developing" && (
              <div>
                <p className="text-[11px] text-slate-500 mb-1.5">Development progress — {form.progress || 0}%</p>
                <input
                  data-testid="app-progress-input"
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={form.progress || 0}
                  onChange={(e) => setForm({ ...form, progress: Number(e.target.value) })}
                  className="w-full accent-[#ED1C24]"
                />
              </div>
            )}
            <Input data-testid="app-url-input" placeholder="App link (https://...)" value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })} className={field} />
            <div>
              <p className="text-[11px] text-slate-500 mb-1.5">Screenshot / logo</p>
              <ImageUploadField testId="app-image" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
            </div>
            <GalleryField testId="app-gallery" value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} />
            <button
              data-testid="app-save-btn"
              onClick={save}
              className="w-full bg-[#ED1C24] hover:bg-[#C81016] text-white font-medium py-3 transition-colors duration-300"
            >
              {editing ? "Save changes" : "Add app"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Package },
  { id: "projects", label: "Projects", icon: FolderKanban },
  { id: "apps", label: "Apps", icon: Layers },
  { id: "brands", label: "Brands", icon: Tags },
  { id: "team", label: "Team", icon: Users },
  { id: "promos", label: "Promo Codes", icon: Percent },
  { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { id: "clients", label: "Clients", icon: Building2 },
  { id: "activities", label: "Activities", icon: GraduationCap },
  { id: "signups", label: "Signups", icon: UserPlus },
  { id: "announcement", label: "Announcement", icon: Megaphone },
  { id: "service_status", label: "Status", icon: Activity },
  { id: "users", label: "Users", icon: UserCog, superOnly: true },
  { id: "settings", label: "Settings", icon: Settings, superOnly: true },
  { id: "emails", label: "Emails", icon: Mail },
  { id: "tickets", label: "Support", icon: LifeBuoy },
  { id: "partners", label: "Partners", icon: Handshake },
  { id: "partner_requests", label: "P. Requests", icon: Inbox },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "messages", label: "Messages", icon: Inbox },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [tab, setTab] = useState("overview");

  if (user === null) {
    return (
      <main className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#ED1C24] border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }
  if (user === false || !["admin", "editor"].includes(user.role)) return <Navigate to="/admin/login" replace />;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#0B1120] flex">
      <aside className="w-16 lg:w-60 shrink-0 bg-[#070C17] border-r border-white/10 flex flex-col fixed inset-y-0">
        <div className="p-5 flex items-center gap-3 border-b border-white/10">
          <span className="w-9 h-9 bg-[#ED1C24] flex items-center justify-center font-display font-bold text-white -skew-x-6 shrink-0">K</span>
          <span className="hidden lg:block font-display font-semibold text-white text-sm">
            KINOS Admin<span className="text-[#ED1C24]">.</span>
          </span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {TABS.filter((t) => !t.superOnly || user.role === "admin").map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-3 py-3 text-sm transition-colors duration-300 ${
                tab === t.id
                  ? "bg-[#ED1C24]/15 text-white border-l-2 border-[#ED1C24]"
                  : "text-slate-400 hover:text-white border-l-2 border-transparent"
              }`}
            >
              <t.icon size={17} className="shrink-0" />
              <span className="hidden lg:inline">{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-3 space-y-1 border-t border-white/10">
          <Link
            data-testid="admin-view-site-link"
            to="/"
            className="flex items-center gap-3 px-3 py-3 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ExternalLink size={17} className="shrink-0" />
            <span className="hidden lg:inline">View Site</span>
          </Link>
          <button
            data-testid="admin-change-password-btn"
            onClick={() => setPwOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-3 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <Lock size={17} className="shrink-0" />
            <span className="hidden lg:inline">Password</span>
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-3 text-sm text-slate-400 hover:text-[#ED1C24] transition-colors"
          >
            <LogOut size={17} className="shrink-0" />
            <span className="hidden lg:inline">Logout</span>
          </button>
        </div>
      </aside>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      <div className="flex-1 ml-16 lg:ml-60 p-6 lg:p-10">
        <div className="flex items-baseline justify-between mb-8">
          <h1 className="font-display font-semibold text-white text-2xl lg:text-3xl capitalize">
            {TABS.find((t) => t.id === tab)?.label || tab}
          </h1>
          <span className="text-xs text-slate-500">Signed in as {user.email}</span>
        </div>
        {tab === "overview" && <Overview />}
        {tab === "products" && <Products />}
        {tab === "projects" && <ProjectManager />}
        {tab === "apps" && <AppsManager />}
        {tab === "brands" && <Brands />}
        {tab === "team" && <TeamManager />}
        {tab === "promos" && <Promos />}
        {tab === "testimonials" && <TestimonialsManager />}
        {tab === "clients" && <ClientsManager />}
        {tab === "activities" && <ActivitiesManager />}
        {tab === "signups" && <Signups />}
        {tab === "announcement" && <Announcement />}
        {tab === "service_status" && <ServiceStatusManager />}
        {tab === "users" && <UserManager />}
        {tab === "settings" && <SettingsTab />}
        {tab === "emails" && <EmailLog />}
        {tab === "tickets" && <Tickets />}
        {tab === "partners" && <Partners />}
        {tab === "partner_requests" && <PartnerRequests />}
        {tab === "orders" && <Orders />}
        {tab === "messages" && <Messages />}
      </div>
    </main>
  );
}
